import React, { useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip,  Filler, } from "chart.js";
import moment from "moment";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const GraphicTrendChart = ({ data, color, startTime, endTime, labels, unit, tooltipId = "custom-tooltip" }) => {
    const chartRef = useRef(null);
    
    const chartData = {
        labels: labels,
        datasets: [
            {
                data: data.map((value) => (value === null || isNaN(value) ? null : value)),
                borderColor: color,
                backgroundColor: color,
                borderWidth: 2,
                pointRadius: 3, // 포인트 크기
                fill: false,
                tension: 0,
                spanGaps: true,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
            x: {
                ticks: {
                    display: false,
                },
                grid: {
                    color: "#4E4E4E",
                    drawTicks: false,  // 틱 표시 제거
                },
            },
            y: {
                ticks: {
                    display: false,
                },
                grid: {
                    color: "#4E4E4E",
                    drawTicks: false,
                },
            },
        },
        plugins: {
            tooltip: {
                enabled: false, // 기본 툴팁 비활성화
                intersect: false,
                external: (context) => {
                    const tooltipModel = context.tooltip;
                    const tooltipEl = document.getElementById(tooltipId);
                    if (tooltipModel.opacity === 0) {
                        tooltipEl.style.opacity = 0;
                        return;
                    }
            
                    // 데이터 설정
                    const chart = chartRef.current;
                    if (!chart) return;
            
                    const dataIndex = tooltipModel.dataPoints[0].dataIndex;
                    const value = tooltipModel.dataPoints[0].raw;
                    const timestamp = labels[dataIndex];
            
                    // 툴팁 위치 계산
                    const chartRect = chart.canvas.getBoundingClientRect(); // 차트 경계
                    const tooltipWidth = tooltipEl.offsetWidth || 100; // 툴팁 가로 크기 (기본값: 100)
                    const tooltipHeight = tooltipEl.offsetHeight || 30; // 툴팁 세로 크기 (기본값: 30)
                    const caretX = tooltipModel.caretX; // 툴팁의 X 좌표
                    const caretY = tooltipModel.caretY; // 툴팁의 Y 좌표
            
                    // 동적 위치 조정 (화면 경계 안에 위치하도록)
                    let left = caretX;
                    let top = caretY;
            
                    // X 좌표가 오른쪽 화면 밖으로 나가는 경우 조정
                    if (caretX + tooltipWidth > chartRect.width) {
                        left = chartRect.width - tooltipWidth - 10; // 우측 여백
                    }
                    // X 좌표가 왼쪽으로 넘어가는 경우 조정
                    if (caretX - tooltipWidth / 2 < 0) {
                        left = tooltipWidth / 2; // 좌측 여백
                    }
            
                    // Y 좌표가 너무 위로 올라가는 경우 조정
                    if (caretY - tooltipHeight < 0) {
                        top = caretY + tooltipHeight + 10; // 아래쪽으로 이동
                    } else {
                        top = caretY - tooltipHeight - 10; // 기본 위치
                    }
            
                    // 툴팁 스타일 업데이트
                    tooltipEl.style.opacity = 1;
                    tooltipEl.style.left = `${left}px`;
                    tooltipEl.style.top = `${top}px`;
                    tooltipEl.style.transform = "translateX(-50%)";
            
                    // 툴팁 내용 업데이트
                    tooltipEl.innerHTML = `
                        <div style="padding: 8px; height: 30px; display: flex; gap:8px; align-items: center; justify-content: center; color: white; background-color: black; border-radius: 4px;">
                            <span style="font-size: 14px; font-weight: 300;">${moment(timestamp).format('HH:mm:ss')}</span>
                            <span style="font-size: 18px; white-space: nowrap; color: ${color}; font-weight: 500; whit">${value} 
                                <span style="font-size: 14px; font-weight: 300;">${unit}</span>
                            </span>
                        </div>
                    `;
                },
            },            
            legend: {
                display: false,
            },
        },
        hover: {
            mode: "index",
            intersect: false,
        },
    };

    useEffect(() => {
        const chartInstance = chartRef.current;

        if (chartInstance) {
            const plugin = {
                id: "verticalLineOnHover",
                afterDraw: (chart) => {
                    if (chart.tooltip._active && chart.tooltip._active.length) {
                        const activePoint = chart.tooltip._active[0];
                        const ctx = chart.ctx;
                        const x = activePoint.element.x;
                        const topY = chart.chartArea.top;
                        const bottomY = chart.chartArea.bottom;

                        // 세로 점선 그리기
                        ctx.save();
                        ctx.beginPath();
                        ctx.moveTo(x, topY);
                        ctx.lineTo(x, bottomY);
                        ctx.lineWidth = 1;
                        ctx.setLineDash([3, 3]);
                        ctx.strokeStyle = "#fff";
                        ctx.stroke();
                        ctx.restore();
                    }
                },
            };

            // 플러그인 등록
            ChartJS.register(plugin);
        }
    }, []);

    // 5초 단위 레이블 생성 함수 (startTime 제외)
    const generateFiveSecondLabels = (startTime, endTime) => {
        const labels = [];
        let current = moment(startTime).add(5, 'seconds').startOf('second'); // startTime + 5초부터 시작
        const end = moment(endTime).startOf('second');
        while (current < end) { // endTime은 제외
            labels.push(current.clone());
            current.add(5, 'seconds');
        }
        return labels;
    };

    const fiveSecondLabels = generateFiveSecondLabels(startTime, endTime);

    // 레이블의 위치 계산
    const startTimestamp = new Date(startTime).getTime();
    const endTimestamp = new Date(endTime).getTime();
    const chartWidth = 2700; // GraphicTrendChart의 실제 너비와 동일하게 설정
    const totalDurationSec = (endTimestamp - startTimestamp) / 1000; // 총 구간 시간(초)
    const pxPerSec = chartWidth / totalDurationSec; // 초당 픽셀 비율

    const labelsWithPosition = fiveSecondLabels.map(labelTime => {
        const elapsedSec = labelTime.diff(moment(startTime), 'seconds');
        const position = elapsedSec * pxPerSec;
        return { time: labelTime, position };
    });

    return (
        <div style={{ position: "relative", height: "100%", width: "100%" }}>
            <Line ref={chartRef} data={chartData} options={options} />
            <div
                id={tooltipId}
                style={{
                    position: "absolute",
                    pointerEvents: "none",
                    opacity: 0,
                    transition: "opacity 0.1s ease",
                }}
            ></div>
            <div className="flex justify-between mb-[7px]">
                <span>{startTime}</span>
                {/* 5초 단위 시간 레이블 렌더링 */}
                <div className="flex justify-between w-[1828px]">
                    {labelsWithPosition.map((label, idx) => (
                        <div key={idx} className=""> {label.time.format("HH:mm:ss")} </div>
                    ))}
                </div>
                <span>{endTime}</span>
            </div>
        </div>
    );
};

export default GraphicTrendChart;
